/**
 * §6 ConfirmDialog 統合: ダイアログの種別とプロパティ生成。
 *
 * index.tsx から抽出した純関数 + 型定義。
 */
import type { OverContractCell } from '~/pages/Supply48Data/hooks/useSupply48EditSession'

/** §6 ConfirmDialog 統合: ダイアログの種別 */
export type ConfirmKind =
  | 'revertLeg'
  | 'revertAll'
  | 'overContract'
  | 'inactiveKoma'
  | 'filterChange'
  | 'blocker'

/**
 * PendingConfirm は kind ごとに必要な付帯情報が異なる (revertLeg/revertAll は
 * AC-S4D-008 の対象件数、inactiveKoma は AC-S4D-045 の警告文言) ため
 * discriminated union で表す。`manualEditCount?: number` / `message?: string`
 * を全 kind 共通の optional field として平坦に持たせると、値が無い kind では
 * `?? 0` / `?? ''` の silent fallback を書かざるを得ず (未設定時に本文が空の
 * まま「登録する」ボタンだけのダイアログが出うる)、型で防げるはずの漏れが
 * 実行時まで残る (2026-08-22 レビュー指摘)。
 */
export type PendingConfirm =
  | {
      kind: 'filterChange' | 'blocker'
      run: () => void
    }
  | {
      /** AC-S4D-018: 契約量を超えるセル (確認ダイアログの件数と一覧の元)。 */
      kind: 'overContract'
      run: () => void
      cells: readonly OverContractCell[]
    }
  | {
      /** AC-S4D-008: リセット確認ダイアログに明示する対象手動変更件数。 */
      kind: 'revertAll'
      run: () => void
      manualEditCount: number
    }
  | {
      kind: 'revertLeg'
      run: () => void
      manualEditCount: number
      /** AC-S4D-008: タイトル「{対象}のリセット」の {対象} (列ヘッダーに出す名前)。 */
      target: string
    }
  | {
      /**
       * AC-S4D-045: 時間帯区分整合の警告文言 (backend が spec どおりに組み立てた
       * サーバー文言、印は除去済み)。
       */
      kind: 'inactiveKoma'
      run: () => void
      message: string
    }
  | null

export type ConfirmDialogProps = {
  title: string
  body: string
  confirmLabel: string
  destructive: boolean
}

/**
 * §6 ConfirmDialog の kind → i18n key 表引き。
 *
 * revertLeg / revertAll の本文には AC-S4D-008 が要求する対象手動変更件数を、
 * overContract の本文には AC-S4D-018 の超過セル件数を `count` で差し込む (英語の単複解決に使う)。
 * inactiveKoma の本文は AC-S4D-018 (契約量超過) と異なり固定 i18n 文言ではなく、
 * backend が組み立てた spec 文言 (message) をそのまま使う (frontend は項目名・
 * 日付・コマ・時間帯区分名を持っていないため)。
 *
 * pendingConfirm (discriminated union) をそのまま受け取り、`switch (kind)` の
 * 型narrowingで各 kind 固有の付帯情報 (manualEditCount / message) を取り出す
 * (旧: 位置引数で渡していたが、kind ごとの必須性を型で表せなかった)。
 */
export function getConfirmDialogProps(
  pendingConfirm: NonNullable<PendingConfirm>,
  t: (key: string, options?: Record<string, unknown>) => string,
): ConfirmDialogProps {
  switch (pendingConfirm.kind) {
    case 'revertLeg':
      return {
        title: t('page:supply48Data.revertColumnConfirm.title', {
          target: pendingConfirm.target,
        }),
        body: t('page:supply48Data.revertColumnConfirm.body', {
          count: pendingConfirm.manualEditCount,
        }),
        confirmLabel: t('page:supply48Data.revertColumnConfirm.confirm'),
        destructive: true,
      }
    case 'revertAll':
      return {
        title: t('page:supply48Data.revertAllConfirm.title'),
        body: t('page:supply48Data.revertAllConfirm.body', {
          count: pendingConfirm.manualEditCount,
        }),
        confirmLabel: t('page:supply48Data.revertAllConfirm.confirm'),
        destructive: true,
      }
    case 'overContract':
      return {
        title: t('page:supply48Data.overContract.title'),
        body: t('page:supply48Data.overContract.body', {
          count: pendingConfirm.cells.length,
        }),
        confirmLabel: t('page:supply48Data.overContract.confirm'),
        destructive: false,
      }
    case 'inactiveKoma':
      return {
        title: t('page:supply48Data.inactiveKomaConfirm.title'),
        body: pendingConfirm.message,
        confirmLabel: t('page:supply48Data.inactiveKomaConfirm.confirm'),
        destructive: false,
      }
    case 'filterChange':
      return {
        title: t('page:supply48Data.filterChangeConfirm.title'),
        body: t('page:supply48Data.filterChangeConfirm.body'),
        confirmLabel: t('page:supply48Data.filterChangeConfirm.confirm'),
        destructive: true,
      }
    case 'blocker':
      return {
        title: t('page:supply48Data.pendingEditsGuard.title'),
        body: t('page:supply48Data.pendingEditsGuard.body'),
        confirmLabel: t('page:supply48Data.pendingEditsGuard.confirm'),
        destructive: true,
      }
  }
}
